import {useMemo, useState} from 'react';
import {graphql} from 'react-relay';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import type {Ticket} from '../types/ticket';
import {SLABadge} from './SLABadge';
import {AssigneePicker} from './AssigneePicker';
import {useMentionUsers} from '../hooks/useMentionUsers';
import {useAuth} from '../../auth/context/AuthContext';
import {hasTicketPerm, TICKET_PERMS} from '../../auth/utils/ticketPerms';
import {DEFAULT_VISIBLE_COLUMN_KEYS, LG_COLUMNS, type ColumnKey} from '../constants/listColumns';
import {formatDateShort} from '../utils/formatDate';

export const TicketRowFragment = graphql`
	fragment TicketRow_ticket on Ticket {
		id
		subject
		status
		priority
		assigneeId
		createdAt
		updatedAt
		slug
		requester {
			id
			name
			email
		}
		assignee {
			fullName
			...ticketingPerson_person
		}
		labels {
			id
			name
			color
		}
		sla {
			breached
			elapsedSeconds
			thresholdSeconds
			paused
		}
	}
`;

interface Props {
	ticket: Ticket;
	density?: 'comfortable' | 'dense';
	selected?: boolean;
	onSelect?: (ticketId: string) => void;
	onAssigneeChange?: (ticketId: string, userId: string | null) => void;
	onDelete?: (ticketId: string) => void;
	/**
	 * Which lg-breakpoint columns are visible. When omitted, every column
	 * renders — matching the row's pre-toggle behavior.
	 */
	visibleColumns?: ReadonlySet<ColumnKey>;
}

const ALL_VISIBLE: ReadonlySet<ColumnKey> = DEFAULT_VISIBLE_COLUMN_KEYS;

// Tailwind md/lg breakpoints, expressed for styled-components media queries.
// Per ACC-523: base styles are the *visible* state and media queries hide,
// because jsdom (and getByRole) treat a base `display:none` as hidden.
const MD = '(max-width: 767px)';
const LG_UP = '(min-width: 1024px)';
const BELOW_LG = '(max-width: 1023px)';

const PRIORITY_COLORS: Record<string, string> = {
	p0: '#ef4444', // red-500
	p1: '#fb923c', // orange-400
	p2: '#fbbf24', // amber-400
	p3: '#cbd5e1', // slate-300
};

const PRIORITY_LABELS: Record<string, string> = {
	p0: 'Urgent',
	p1: 'High',
	p2: 'Medium',
	p3: 'Low',
};

// Tailwind *-500 avatar palette, ported to hex.
const AVATAR_COLORS = [
	'#6366f1', // indigo-500
	'#10b981', // emerald-500
	'#f59e0b', // amber-500
	'#f43f5e', // rose-500
	'#0ea5e9', // sky-500
	'#a855f7', // purple-500
	'#14b8a6', // teal-500
	'#ec4899', // pink-500
];

// border-left + background pairs for the status accent stripe.
const STATUS_COLORS: Record<string, {border: string; bg: string}> = {
	new: {border: '#fb923c', bg: '#fff7ed'}, // orange-400 / orange-50
	open: {border: '#34d399', bg: '#ecfdf5'}, // emerald-400 / emerald-50
	in_progress: {border: '#818cf8', bg: '#eef2ff'}, // indigo-400 / indigo-50
	waiting_on_customer: {border: '#fbbf24', bg: '#fffbeb'}, // amber-400 / amber-50
	blocked: {border: '#fb7185', bg: '#fff1f2'}, // rose-400 / rose-50
	resolved: {border: '#cbd5e1', bg: '#ffffff'}, // slate-300 / white
	closed: {border: '#cbd5e1', bg: '#ffffff'},
};

function getAvatarColor(name: string): string {
	let hash = 0;
	for (let i = 0; i < name.length; i++) {
		hash = name.charCodeAt(i) + ((hash << 5) - hash);
	}
	return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]!;
}

function getInitials(name: string): string {
	const parts = name.trim().split(/\s+/);
	if (parts.length >= 2) {
		return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
	}
	return name.slice(0, 2).toUpperCase();
}

const RowLink = styled(Link)<{$mdTemplate: string; $selected: boolean; $borderColor: string}>`
	display: grid;
	grid-template-columns: ${p => p.$mdTemplate};
	align-items: center;
	gap: 12px;
	padding: 8px 16px;
	border-bottom: 1px solid #f1f5f9;
	border-left: 2px solid ${p => p.$borderColor};
	background: ${p => (p.$selected ? p.theme.sys.color.background.primary['1'] : 'transparent')};
	${p => p.theme.sys.font?.body?.['2']}
	color: #1e293b;
	text-decoration: none;
	transition: background-color 0.15s;

	&:hover {
		background: ${p =>
			p.$selected ? p.theme.sys.color.background.primary['1'] : p.theme.sys.color.background.disabled['1']};
	}

	@media ${LG_UP} {
		grid-template-columns: var(--lg-cols);
	}
	@media ${MD} {
		display: none;
	}
`;

const CheckboxCell = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
`;

const RowCheckbox = styled.input`
	height: 16px;
	width: 16px;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	accent-color: #4f46e5;
`;

const IdCell = styled.span`
	${p => p.theme.sys.font?.tags?.['1']}
	font-size: 14px;
	line-height: 20px;
	font-weight: 400;
	color: #94a3b8;
`;

const SubjectCell = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
`;

const SubjectText = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-weight: 500;
	color: #1e293b;
`;

const MdCell = styled.div<{$hideLg?: boolean}>`
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
	${p =>
		p.$hideLg &&
		`@media ${LG_UP} {
			display: none;
		}`}
`;

const AssigneeCell = styled(MdCell)`
	position: relative;
`;

const PriorityCell = styled.div<{$hideLg?: boolean}>`
	display: flex;
	align-items: center;
	gap: 6px;
	${p =>
		p.$hideLg &&
		`@media ${LG_UP} {
			display: none;
		}`}
`;

const MutedSmall = styled.span<{$color: string}>`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	${p => p.theme.sys.font?.detail?.['1']}
	color: ${p => p.$color};
`;

const PriorityDot = styled.span<{$color: string}>`
	height: 8px;
	width: 8px;
	border-radius: 9999px;
	background: ${p => p.$color};
`;

const PriorityLabel = styled.span`
	${p => p.theme.sys.font?.detail?.['1']}
	color: #475569;
`;

const AvatarBase = styled.span<{$bg: string; $text: string; $dashed?: boolean}>`
	display: inline-flex;
	height: 24px;
	width: 24px;
	align-items: center;
	justify-content: center;
	border-radius: 9999px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	background: ${p => p.$bg};
	color: ${p => p.$text};
	${p => p.$dashed && 'border: 1px dashed #cbd5e1;'}
`;

// hidden below lg, block at lg — base is the visible (block) state.
const LgOnly = styled.span`
	display: block;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #94a3b8;
	@media ${BELOW_LG} {
		display: none;
	}
`;

const ProjectPlaceholder = styled(LgOnly)`
	color: #cbd5e1;
`;

const SlaCell = styled.span`
	display: block;
	@media ${BELOW_LG} {
		display: none;
	}
`;

const LabelsCell = styled.div`
	display: flex;
	min-width: 0;
	flex-wrap: wrap;
	align-items: center;
	gap: 4px;
	@media ${BELOW_LG} {
		display: none;
	}
`;

const LabelChip = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	border-radius: 9999px;
	background: #f1f5f9;
	padding: 2px 8px;
	${p => p.theme.sys.font?.detail?.['1']}
`;

const LabelDot = styled.span`
	display: inline-block;
	height: 8px;
	width: 8px;
	flex-shrink: 0;
	border-radius: 9999px;
`;

const LabelName = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const EmptyDash = styled.span`
	${p => p.theme.sys.font?.detail?.['1']}
	color: #cbd5e1;
`;

const DeleteCell = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	@media ${BELOW_LG} {
		display: none;
	}
`;

const DeleteButton = styled.button`
	display: flex;
	height: 24px;
	width: 24px;
	align-items: center;
	justify-content: center;
	border: 0;
	border-radius: 4px;
	background: transparent;
	color: #94a3b8;
	opacity: 0;
	cursor: pointer;
	transition: opacity 0.15s;

	${RowLink}:hover & {
		opacity: 1;
	}
	&:hover {
		background: #f1f5f9;
		color: #475569;
	}
`;

const DeleteIcon = styled.svg`
	height: 16px;
	width: 16px;
`;

function RequesterAvatar({name}: {name: string | null}) {
	if (!name) {
		return (
			<AvatarBase $bg="#e2e8f0" $text="#64748b">
				?
			</AvatarBase>
		);
	}
	return (
		<AvatarBase $bg={getAvatarColor(name)} $text="#ffffff" title={name}>
			{getInitials(name)}
		</AvatarBase>
	);
}

function AssigneeAvatar({name, onClick}: {name: string | null; onClick?: (e: React.MouseEvent) => void}) {
	if (!name) {
		return (
			<AvatarBase $bg="#e2e8f0" $text="#94a3b8" $dashed onClick={onClick} role={onClick ? 'button' : undefined}>
				?
			</AvatarBase>
		);
	}
	return (
		<AvatarBase
			$bg={getAvatarColor(name)}
			$text="#ffffff"
			title={name}
			onClick={onClick}
			role={onClick ? 'button' : undefined}
		>
			{getInitials(name)}
		</AvatarBase>
	);
}

export function TicketRow({ticket, selected, onSelect, onAssigneeChange, onDelete, visibleColumns}: Props) {
	const {user} = useAuth();
	const [pickerOpen, setPickerOpen] = useState(false);
	const {users} = useMentionUsers();
	const visible = visibleColumns ?? ALL_VISIBLE;
	// ACC-440: a leading selection-checkbox column is added only when the row is
	// selectable (the route passes onSelect only with TICKETS_ASSIGN), so the
	// non-selectable layout — and the tests that assert it — is unchanged.
	const selectable = !!onSelect;
	// ACC-548: the kebab/actions button is an extra grid item with no track of
	// its own, so without a trailing column it wraps onto a new auto-row and
	// makes each entry taller than it needs to be. Append a track for it, but
	// only when it actually renders (same condition as the cell below), so a
	// view-only viewer doesn't get a wasted empty column.
	const showDelete = !!(user && hasTicketPerm(user.ticketPerms, TICKET_PERMS.MANAGE) && onDelete);
	const lgTemplate = useMemo(
		() =>
			LG_COLUMNS.filter(c => visible.has(c.key))
				.map(c => c.width)
				.join(' '),
		[visible]
	);
	// The actions cell only renders at lg (it's display:none below lg), so the
	// extra track belongs to the lg template only — adding it to the md template
	// would leave an empty trailing column at the md breakpoint.
	const deleteTrack = showDelete ? ' 2rem' : '';
	const lgColsVar = `${selectable ? '2rem ' : ''}${lgTemplate}${deleteTrack}`;
	const mdTemplate = selectable ? '2rem 4rem 1fr 150px 140px 100px' : '4rem 1fr 150px 140px 100px';
	const requesterVisibleLg = visible.has('requester');
	const assigneeVisibleLg = visible.has('assignee');
	const priorityVisibleLg = visible.has('priority');
	const createdVisibleLg = visible.has('created');
	const labelsVisibleLg = visible.has('labels');

	function handleAvatarClick(e: React.MouseEvent) {
		if (onAssigneeChange) {
			e.preventDefault();
			e.stopPropagation();
			setPickerOpen(true);
		}
	}

	function handleAssigneeSelect(userId: string | null) {
		onAssigneeChange?.(ticket.id, userId);
		setPickerOpen(false);
	}

	const createdDate = new Date(ticket.created_at);
	const createdStr = formatDateShort(ticket.created_at);
	const isToday = new Date().toDateString() === createdDate.toDateString();
	const timeStr = createdDate.toLocaleTimeString('en-US', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	});

	const status = STATUS_COLORS[ticket.status];

	return (
		<RowLink
			to={`/tickets/${ticket.slug}`}
			aria-label={`Ticket ${ticket.slug}: ${ticket.subject}`}
			style={{['--lg-cols' as string]: lgColsVar}}
			$mdTemplate={mdTemplate}
			$selected={!!selected}
			$borderColor={status?.border ?? 'transparent'}
		>
			{/* Selection checkbox */}
			{selectable && (
				<CheckboxCell onClick={e => e.stopPropagation()}>
					<RowCheckbox
						type="checkbox"
						checked={!!selected}
						onChange={() => onSelect?.(ticket.id)}
						onClick={e => e.stopPropagation()}
						aria-label={`Select ticket ${ticket.slug}`}
					/>
				</CheckboxCell>
			)}

			{/* ID */}
			<IdCell>{ticket.slug}</IdCell>

			{/* Subject */}
			<SubjectCell>
				<SubjectText title={ticket.subject}>{ticket.subject}</SubjectText>
			</SubjectCell>

			{/* Requester */}
			<MdCell $hideLg={!requesterVisibleLg}>
				<RequesterAvatar name={ticket.requester_name} />
				<MutedSmall $color="#475569">{ticket.requester_name ?? '—'}</MutedSmall>
			</MdCell>

			{/* Assignee */}
			<AssigneeCell $hideLg={!assigneeVisibleLg}>
				{pickerOpen ? (
					<div
						onClick={e => {
							e.preventDefault();
							e.stopPropagation();
						}}
					>
						<AssigneePicker
							assigneeId={ticket.assignee_id}
							users={users}
							disabled={false}
							defaultOpen={true}
							onChange={userId => handleAssigneeSelect(userId)}
						/>
					</div>
				) : (
					<>
						<AssigneeAvatar
							name={ticket.assignee_name ?? null}
							onClick={onAssigneeChange ? handleAvatarClick : undefined}
						/>
						<MutedSmall $color="#64748b">{ticket.assignee_name ?? 'Unassigned'}</MutedSmall>
					</>
				)}
			</AssigneeCell>

			{/* Priority */}
			<PriorityCell $hideLg={!priorityVisibleLg}>
				<PriorityDot $color={PRIORITY_COLORS[ticket.priority] ?? '#cbd5e1'} />
				<PriorityLabel>{PRIORITY_LABELS[ticket.priority]}</PriorityLabel>
			</PriorityCell>

			{/* Created */}
			{createdVisibleLg && <LgOnly>{isToday ? `today ${timeStr}` : createdStr}</LgOnly>}

			{/* Labels */}
			{labelsVisibleLg && (
				<LabelsCell>
					{ticket.labels.length > 0 ? (
						ticket.labels.map(label => (
							<LabelChip key={label.id} title={label.name}>
								<LabelDot style={{backgroundColor: label.color}} />
								<LabelName>{label.name}</LabelName>
							</LabelChip>
						))
					) : (
						<EmptyDash>—</EmptyDash>
					)}
				</LabelsCell>
			)}

			{/* Project (empty placeholder) */}
			<ProjectPlaceholder>—</ProjectPlaceholder>

			{/* SLA */}
			<SlaCell>
				<SLABadge ticket={ticket} />
			</SlaCell>

			{/* Delete action */}
			{showDelete && onDelete && (
				<DeleteCell
					onClick={e => {
						e.preventDefault();
						e.stopPropagation();
					}}
				>
					<DeleteButton
						type="button"
						aria-label="Ticket actions"
						onClick={e => {
							e.preventDefault();
							e.stopPropagation();
							onDelete(ticket.id);
						}}
					>
						<DeleteIcon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
							<circle cx="10" cy="4" r="1.5" />
							<circle cx="10" cy="10" r="1.5" />
							<circle cx="10" cy="16" r="1.5" />
						</DeleteIcon>
					</DeleteButton>
				</DeleteCell>
			)}
		</RowLink>
	);
}
